import { defineStore } from 'pinia'
import {
  computed,
  getCurrentScope,
  onScopeDispose,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

import { CreativeWatchlistApi } from '../api/CreativeWatchlistApi'
import type {
  WatchlistCreative,
  WatchlistFilter,
  WatchlistOrdering,
  WatchlistPagination,
  WatchlistQuery,
} from '../models/creativeWatchlist.types'
import { WATCHLIST_PAGE_SIZE } from '../models/creativeWatchlist.types'

export type CreativeWatchlistState =
  'idle' | 'loading' | 'loadingMore' | 'empty' | 'ready' | 'error'

export interface CreativeWatchlistSource {
  listGoogleCreatives: (
    accountId: number,
    workspaceId: number,
    query: WatchlistQuery,
    signal?: AbortSignal,
  ) => Promise<{ data: WatchlistCreative[]; pagination: WatchlistPagination }>
}

export interface CreativeWatchlistContext {
  accountId: () => number | undefined
  workspaceId: () => number | undefined
  source?: CreativeWatchlistSource
  debounceMs?: number
}

export interface CreativeWatchlistController {
  items: Ref<WatchlistCreative[]>
  pagination: Ref<WatchlistPagination>
  query: Ref<string>
  filters: Ref<WatchlistFilter[]>
  ordering: Ref<WatchlistOrdering>
  state: Ref<CreativeWatchlistState>
  error: Ref<unknown>
  hasMore: ComputedRef<boolean>
  refresh: () => Promise<void>
  loadMore: () => Promise<void>
  reset: () => void
  dispose: () => void
}

/** Headless state machine; the Pinia store below owns its production lifetime. */
export function createCreativeWatchlist(
  context: CreativeWatchlistContext,
): CreativeWatchlistController {
  const source = context.source ?? new CreativeWatchlistApi()
  const items = ref<WatchlistCreative[]>([])
  const pagination = ref<WatchlistPagination>({})
  const query = ref('')
  const filters = ref<WatchlistFilter[]>([])
  const ordering = ref<WatchlistOrdering>('-last_shown')
  const state = ref<CreativeWatchlistState>('idle')
  const error = ref<unknown>(null)
  let page = 1
  let requestGeneration = 0
  let controller: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const hasMore = computed(() => {
    const total = pagination.value.total
    return total === undefined ? Boolean(pagination.value.next) : items.value.length < total
  })

  function clearTimer(): void {
    if (debounceTimer !== null) clearTimeout(debounceTimer)
    debounceTimer = null
  }

  function abortRequest(): void {
    requestGeneration += 1
    controller?.abort()
    controller = null
  }

  function requestQuery(targetPage: number): WatchlistQuery {
    return {
      page: targetPage,
      page_size: WATCHLIST_PAGE_SIZE,
      ordering: ordering.value,
      ...(query.value.trim() ? { search: query.value.trim() } : {}),
      ...(filters.value.length ? { filters: filters.value } : {}),
    }
  }

  async function load(targetPage: number, append: boolean): Promise<void> {
    const accountId = context.accountId()
    const workspaceId = context.workspaceId()
    if (accountId === undefined || workspaceId === undefined) {
      abortRequest()
      items.value = []
      pagination.value = {}
      state.value = 'idle'
      return
    }

    abortRequest()
    const generation = requestGeneration
    controller = new AbortController()
    state.value = append ? 'loadingMore' : 'loading'
    error.value = null

    try {
      const result = await source.listGoogleCreatives(
        accountId,
        workspaceId,
        requestQuery(targetPage),
        controller.signal,
      )
      if (generation !== requestGeneration) return
      items.value = append ? [...items.value, ...result.data] : result.data
      pagination.value = result.pagination
      page = targetPage
      state.value = items.value.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (generation !== requestGeneration || controller.signal.aborted) return
      error.value = cause
      state.value = 'error'
    } finally {
      if (generation === requestGeneration) controller = null
    }
  }

  async function refresh(): Promise<void> {
    clearTimer()
    page = 1
    await load(1, false)
  }

  async function loadMore(): Promise<void> {
    if (state.value === 'loading' || state.value === 'loadingMore' || !hasMore.value) return
    await load(page + 1, true)
  }

  function scheduleRefresh(): void {
    clearTimer()
    abortRequest()
    debounceTimer = setTimeout(() => void refresh(), context.debounceMs ?? 400)
  }

  const stopControls = watch([query, filters, ordering], scheduleRefresh, { deep: true })
  const stopContext = watch(
    [context.accountId, context.workspaceId],
    () => {
      items.value = []
      pagination.value = {}
      void refresh()
    },
    { immediate: true },
  )

  /** Back to the pristine machine, without tearing the watchers down (`dispose` does that). Any
   *  in-flight page is aborted and its generation retired, so a late response cannot repopulate
   *  the list the caller just cleared. */
  function reset(): void {
    clearTimer()
    abortRequest()
    page = 1
    items.value = []
    pagination.value = {}
    query.value = ''
    filters.value = []
    ordering.value = '-last_shown'
    state.value = 'idle'
    error.value = null
  }

  function dispose(): void {
    clearTimer()
    abortRequest()
    stopControls()
    stopContext()
  }
  if (getCurrentScope()) onScopeDispose(dispose)

  return {
    items,
    pagination,
    query,
    filters,
    ordering,
    state,
    error,
    hasMore,
    refresh,
    loadMore,
    reset,
    dispose,
  }
}

/**
 * The route-independent owner. Meta/Google tabs use `v-show`, but the store also preserves the
 * list if route chrome temporarily unmounts the Google panel and rejects stale workspace reads.
 */
export const useCreativeWatchlistStore = defineStore('creativeWatchlist', () => {
  const accountId = ref<number>()
  const workspaceId = ref<number>()
  const source = shallowRef<CreativeWatchlistSource>(new CreativeWatchlistApi())
  const controller = createCreativeWatchlist({
    accountId: () => accountId.value,
    workspaceId: () => workspaceId.value,
    source: {
      listGoogleCreatives: (...args) => source.value.listGoogleCreatives(...args),
    },
  })

  function selectContext(
    nextAccountId: number | undefined,
    nextWorkspaceId: number | undefined,
  ): void {
    if (accountId.value === nextAccountId && workspaceId.value === nextWorkspaceId) return
    accountId.value = nextAccountId
    workspaceId.value = nextWorkspaceId
  }

  /** Test-only injection seam; production keeps the typed API instance above. */
  function setSource(next: CreativeWatchlistSource): void {
    source.value = next
  }

  /** Workspace switch / session teardown (contracts 06 §7, 07 §7). Drops the cached context too,
   *  so the next `selectContext` re-arms the fetch even when it names the same ids. */
  function reset(): void {
    accountId.value = undefined
    workspaceId.value = undefined
    controller.reset()
  }

  return { ...controller, selectContext, setSource, reset }
})
